import { Timestamp } from 'firebase/firestore';
import { IRoute, Material } from '../../../sp-types';
import { IPositionView, IPositionMaterial } from '../../../sp-types/financialPositionType';
import { routeColors } from '../../Policy/MaterialDetails/components/MaterialDetailUtils';

export const mapFilterPositions = (
  positions: IPositionView[],
  materialId: string,
  routesMap: Record<string, IRoute>,
  materialPrice: number,
  showExpired: boolean = false,
): IPositionView[] => {
  const now = new Date();

  const routeGroups = groupPositionsByRoute(positions, materialId, showExpired ? undefined : now);

  const result = Object.entries(routeGroups).map(
    ([routeId, group]: [string, IPositionView[]], i): IPositionView => ({
      routeId,
      id: routeId,
      accountId: group[0].accountId,
      activityId: 'many',
      contracts: [],
      materialId,
      positionType: 'long',
      routeName: routesMap[routeId]?.name || '',
      color: routeColors[i % routeColors.length],
      exposureCalendar: group.flatMap((a) => a.exposureCalendar || ({} as any)) || [],
      positions: group
        .map((pos) => ({
          ...pos,
          isDisabled: pos.expiryDate.toDate() < now,
          color: routeColors[i % routeColors.length],
          routeName: routesMap[routeId]?.name || '',
          totalPrice: pos.routeQuantity * (pos.positionType === 'long' ? 1 : -1) * materialPrice || 0, // should be deleted - we need to use the exposureCalendar
        }))
        .sort((a, b) => a.expiryDate.seconds - b.expiryDate.seconds),
      createDate: Timestamp.fromDate(
        group.reduce((res, { createDate }) => (res < createDate.toDate() ? res : createDate.toDate()), now),
      ),
      routeQuantity: group
        .filter(({ expiryDate }) => now < expiryDate?.toDate())
        .reduce((res, { routeQuantity, positionType }) => res + routeQuantity * (positionType === 'long' ? 1 : -1), 0),

      expiryDate: Timestamp.fromDate(
        group.reduce((res, { expiryDate }) => (res > expiryDate.toDate() ? res : expiryDate.toDate()), now),
      ),

      totalPrice:
        group
          .filter((p) => now < p.expiryDate?.toDate())
          .reduce(
            (res, { routeQuantity, positionType }) => res + routeQuantity * (positionType === 'long' ? 1 : -1),
            0,
          ) * materialPrice || 0,
    }),
  );

  return result;
};

const groupPositionsByRoute = (
  positions: IPositionView[],
  materialId: string,
  fromDate?: Date,
): Record<string, IPositionView[]> =>
  positions
    ?.filter((a) => a.materialId === materialId && (!fromDate || fromDate <= a.expiryDate?.toDate()))
    .reduce((res, position) => {
      res[position.routeId] = [...(res[position.routeId] || []), position];
      return res;
    }, {} as Record<string, IPositionView[]>);

const monthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
export const positionsByMaterial = (
  positions: IPositionView[],
  showExpired: boolean = false,
): Record<string, IPositionView[]> =>
  positions
    .filter((a) => showExpired || monthStart(new Date()) <= a.expiryDate?.toDate())
    .reduce(
      (res, pos) => ({
        ...res,
        [pos.materialId]: [...(res[pos.materialId] || []), pos],
      }),
      {} as Record<string, IPositionView[]>,
    );
const getMinTimestamp = (seconds: number[]): Timestamp => new Timestamp(Math.min(...seconds), 0);
const getMaxTimestamp = (seconds: number[]): Timestamp => new Timestamp(Math.max(...seconds), 0);

const getRoutesPrice = (positions: IPositionView[], pricesMap: Record<string, number>): number =>
  positions.reduce(
    (sum, { routeQuantity, materialId, positionType }) =>
      sum + routeQuantity * pricesMap[materialId] * (positionType === 'long' ? 1 : -1),
    0,
  );

export const aggregateByMaterial = (
  materialPositions: Record<string, IPositionView[]>,
  materialsMap: Record<string, Material>,
  colorMap: Record<string, string>,
  pricesMap: Record<string, number>,
): IPositionMaterial[] =>
  Object.entries(materialPositions).map(([materialId, positions]) => ({
    id: positions?.[0]?.id,
    accountId: positions?.[0]?.accountId,
    materialId,
    unit: materialsMap[materialId]?.unit,
    price: pricesMap[materialId] || 0,
    positions: positions?.map(
      (p) =>
        ({
          ...p,
          materialName: materialsMap[materialId]?.name,
          color: colorMap[materialId],
          totalPrice: p.routeQuantity * (pricesMap[materialId] || 1) * (p.positionType === 'long' ? 1 : -1),
        } as IPositionMaterial),
    ),
    materialName: materialsMap[materialId]?.name,
    color: colorMap[materialId],
    createDate: getMinTimestamp(positions.map(({ createDate }) => createDate.seconds)),
    expiryDate: getMaxTimestamp(positions.map(({ expiryDate }) => expiryDate.seconds)),
    routeId: 'multi',
    totalPrice: getRoutesPrice(positions, pricesMap),
    positionType: 'long' as const, // TODO: calculate it
    activityId: '',
    routeQuantity: positions.length,
    contracts: [],
  }));
